"use client";

import React from "react";
import { Facebook, Twitter, Linkedin, Instagram, ArrowUp, Mail, MapPin, Phone, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
const pillars = [{
  title: "Entrepreneurship & Economic Development",
  href: "/pillars/economic-development"
}, {
  title: "Leadership Development",
  href: "/pillars/leadership"
}, {
  title: "Workforce & Innovation",
  href: "/pillars/workforce"
}, {
  title: "Policy & Advocacy",
  href: "/pillars/policy"
}, {
  title: "Community & Cultural Programs",
  href: "/pillars/community"
}, {
  title: "Data & Research",
  href: "/pillars/data-research"
}];
const socialLinks = [{
  icon: Facebook,
  href: "https://facebook.com",
  label: "Facebook"
}, {
  icon: Twitter,
  href: "https://twitter.com",
  label: "Twitter"
}, {
  icon: Linkedin,
  href: "https://linkedin.com",
  label: "LinkedIn"
}, {
  icon: Instagram,
  href: "https://instagram.com",
  label: "Instagram"
}];
function NewsletterForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    successMessage
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="p-4 rounded-md bg-secondary/10 border border-secondary/20 text-secondary text-sm">
        {successMessage || "Thank you for subscribing!"}
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-2">
      <div className="flex gap-2">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem className="flex-1">
              <FormControl>
                <Input placeholder="Enter your email" className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-secondary focus-visible:border-secondary h-10" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <Button type="submit" size="icon" disabled={isSubmitting} className="bg-secondary text-secondary-foreground hover:bg-secondary/90 h-10 w-10 shrink-0">
          <Send className="size-4" />
          <span className="sr-only">Subscribe</span>
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Join our executive briefing. No spam, just impact.
      </p>
    </Form>;
}
export default function FooterInformation() {
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };
  const currentYear = new Date().getFullYear();
  return <section id="footer-information" className="dark bg-background text-foreground border-t border-white/5">
      <div className="container mx-auto px-4 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-8">
          {}
          <div className="space-y-6">
            <Link to="/" className="block">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1208726/img/cohcc_logo_white.png" alt="Colorado Hispanic Chamber of Commerce" className="h-12 w-auto object-contain" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              Founded in 1978, we are a dynamic platform for economic development, leadership, and policy. Empowering the Hispanic business community with data-driven insights and visionary advocacy.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social, i) => <Link key={i} to={social.href} newTab className="text-muted-foreground hover:text-secondary transition-colors duration-300" aria-label={social.label} data-index={i}>
                  <social.icon className="size-5" />
                </Link>)}
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold tracking-tight text-white">Our Pillars</h3>
            <ul className="space-y-3">
              {pillars.map((pillar, i) => <li key={i} data-index={i}>
                  <Link to={pillar.href} className="text-sm text-muted-foreground hover:text-secondary transition-colors duration-200 flex items-center gap-2 group">
                    <span className="h-px w-0 bg-secondary transition-all duration-300 group-hover:w-3" />
                    {pillar.title}
                  </Link>
                </li>)}
            </ul>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold tracking-tight text-white">Contact Us</h3>
            <div className="space-y-4 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <MapPin className="size-5 text-secondary shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-medium text-white">Headquarters</p>
                  <p>Colorado, USA</p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <Phone className="size-5 text-secondary shrink-0" />
                <Link to="tel:+13035551234" className="hover:text-white transition-colors">
                  +1 (303) 555-1234
                </Link>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="size-5 text-secondary shrink-0" />
                <Link to="mailto:info@cohcc.org" className="hover:text-white transition-colors">
                  info@cohcc.org
                </Link>
              </div>

              <div className="pt-2">
                <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                  Bilingual Support (EN/ES)
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="space-y-6">
            <h3 className="text-lg font-semibold tracking-tight text-white">Stay Informed</h3>
            <p className="text-sm text-muted-foreground">
              Subscribe to receive the latest economic data, policy updates, and event invitations.
            </p>
            
            <FormProvider formId="newsletter-footer" sectionName="footer" defaultValues={{
            email: ""
          }} submitText="Subscribe" successMessage="You've been subscribed to our newsletter.">
              <NewsletterForm />
            </FormProvider>
          </div>
        </div>

        {}
        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
            <p>&copy; {currentYear} Colorado Hispanic Chamber of Commerce. All rights reserved.</p>
            <div className="flex gap-4">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
          
          <Button variant="ghost" size="sm" onClick={scrollToTop} className="text-muted-foreground hover:text-secondary hover:bg-transparent gap-2 group">
            Back to Top
            <ArrowUp className="size-4 transition-transform duration-300 group-hover:-translate-y-1" />
          </Button>
        </div>
      </div>
    </section>;
}